/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { Component } from "react";
import { getQueryParameterFromUrl } from "dc-preview-utils/src/Utils/UrlUtils";
import { shouldEnableBookmarks } from "selectors/bookmarksSelectors";
import { getShouldRenderAcroForm } from "selectors/documentSelectors";
import {
  getShouldUseModernViewer,
  getShouldUseMobileModernViewer,
  getShouldEnableOutline,
  getShouldEnableQnA
} from "selectors/appSelectors";
import { getShouldRenderPersonalCommentsDropin } from "selectors/overlayDropinSelectors";
import { isCurrentDeviceMobile } from "dc-preview-utils/src/Utils/DeviceUtils";
import {
  VIEWING_ORIGIN,
  VIEW_SDK_SCROLL,
  IMAGE_SELECTION_PARAMS,
  EDIT_FRICTIONLESS_EXPERIMENT_TARGET_ID
} from "constants/constants";
import {
  getAssetType,
  getSelectedDropinForRHP,
  isPdfRequestSignaturesEnabled,
  isFillSignEnabled,
  getQueryParamValue
} from "app/AppInitUtility";
import { getViewMode, getIsInPlaceMode } from "selectors/viewModeSelectors";
import { isUndefined, isBoolean, isNull, debounce } from "utils/lodashUtils";
import { docViewModes, appIdentifiers, rhpDropinIds } from "constants/enums";
import { isPersonalCommentingEnabled, shouldEnableProgressiverendering } from "utils/FeatureFlagUtils";
import { setShowCommentsPanel } from "utils/PageUtils";

import { isNumber, isNonEmptyString } from "utils/APIUtils";
import { getPagesViewYPos } from "selectors/pagedViewState";
import { getShouldRenderHighlightsDropin } from "selectors/overlayDropinSelectors";
import { isExternalAsset } from "utils/AssetUtils";
import { getDocumentVisibleHeight } from "selectors/documentViewSelectors";

import { getUIControlCustomizer } from "publicApis/UIConfig";
import { setFeatureEnability } from "publicApis/featureConfig";
import { allowedPageViewModes, allowedDocViewModes, lhpFeaturesForDefaultView } from "publicApis/constants";
import { getDefaultConfig, isHexColor } from "./AppConfigProp";
import { envUtil } from "dc-core";
import { setPageViewMode } from "utils/DocumentViewUtils";
import { isFirefox } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { trackDocumenOpenedInNightMode } from "analytics/events";
import { getFeatureFlagApis } from "../publicApis/featureFlag";

class EnableFeatureController extends Component {
  constructor(stores, props) {
    super(props);
    this.stores = stores;
    this.props = props;
    this.showHudOnScrollStop = null;
    this._viewingOrigin = getQueryParameterFromUrl(window.location.href, VIEWING_ORIGIN);
    this._testApiHandlerPromise = null;
  }

  initFeatures() {
    this._setShowHudOnScrollTop();
    this._updateFeaturesFromOldProps(this.props);
    this._updateFeaturesFromProps(this.props);
    this._updateFeaturesFromQueryParams(this.props.config);
    this.setStickyTools();
    this._initProgressiveRenderingSettings(this.props.config);
    this._setShouldShowBottomBanner(this.props.config);
  }

  setStickyTools() {
    const stickyTools = (this.props.config && this.props.config.stickyTools) || [];
    this.stores.stickyToolsStore.setStickyVerbs(stickyTools);
  }

  unregisterScrollEventFromViewSDK() {
    if (this.scrollEventObj) {
      this.scrollEventObj.unregister(VIEW_SDK_SCROLL, this._handleScrollEventFromViewSDK.bind(this));
    }
  }

  _updateFeaturesFromQueryParams(config) {
    this._exposeTestApis();
    this._enableSlideMode();
    this._enableAcroForm();
    this._shouldEnablePersonalCommenting();
    this._enableTouchContextMenu(config);
    this._enableBookmarks();
    this._enableFillSign(config);
    this._enablePdfRequestSignatures(config);
    this._setUseReactVirtualized(config);
    this._enableTiling();
    this._setUndoButtonVisibility(config);
    this._setRedoButtonVisibility(config);
    this._setShowScrubber();
    this._enableTrove();
    this._enableMSAL();
    this._setShouldEnableVoiceAnnotations();
    this._enableLiquidModeView();
    this._setDXVersion();
    this._setUseMinifiedDX();
    this._enableEncryptedWrite(config);
    this._enableFitOneFullPageMode();
    this._suppressPasswordDialog(config);
    this._setFallbackPrintTimeout(config);
    this._enableCopyWithFormatting();
    this._setStickyToolPanelBehavior(config);
    this._enableDarkModeConfigs(config);
    this._enableDarkPageTheme();
    this._enableImageSelection();
  }

  _updateFeaturesFromOldProps(props) {
    const { config } = props;
    if (!isUndefined(config)) {
      const {
        showLHP,
        showHUD,
        shouldRenderCommentsDropin,
        shouldRenderSignAuthoringDropin,
        shouldRenderSignAuthoring2Dropin,
        shouldRenderPersonalCommentsDropin,
        shouldRenderESignOverlayDropin,
        shouldRenderHighlightsDropin,
        shouldRenderVisualizerDropin,
        shouldRenderPageShadows,
        shouldDockHUD,
        shouldDocumentTakeFocus,
        shouldShowDownloadButtonInHud,
        shouldShowSearchInHud,
        shouldRenderAcroForm,
        path,
        docViewBgColor,
        rhpSkeletonDropinId
      } = config;

      this._setDocViewBgColor(docViewBgColor);
      this._setAcroFormVisibility(shouldRenderAcroForm);
      this._setLHPVisibility(showLHP);
      this._setHudDisable(showHUD);

      if (shouldDocumentTakeFocus) {
        window.focus();
      }

      this._setShouldRenderHighlightsDropin(path, shouldRenderHighlightsDropin);
      this._setUseReactVirtualizedAndShouldRenderCommentsDropin(shouldRenderCommentsDropin);
      this._setShouldRenderSignAuthoringDropin(shouldRenderSignAuthoringDropin);
      this._setShouldRenderSignAuthoring2Dropin(shouldRenderSignAuthoring2Dropin);
      this._setShouldRenderPersonalCommentsDropin(shouldRenderPersonalCommentsDropin);
      this._setShouldRenderESignOverlayDropin(shouldRenderESignOverlayDropin);
      this._setShouldRenderVisualizerDropin(shouldRenderVisualizerDropin);
      this._setShouldRenderPageShadows(shouldRenderPageShadows);
      this._setIsDocked(shouldDockHUD);
      this._setDownloadButtonVisibility(shouldShowDownloadButtonInHud);
      this._setUseReactVirtualizedAndFetchAccContent(config);
      this._setShouldshowSearchInHud(shouldShowSearchInHud);
      this._setEnableRHPResizing(config);
      this._setShouldDeferLoadOverlays(config);
      this._setRhpSkeletonDropinId(rhpSkeletonDropinId);
      this._setShouldRenderNotificationBannerBeforeDoc(config);
    }
    this._setOpenAction();
  }

  _updateFeaturesFromProps(props) {
    const { appConfig, UIControlsConfig, documentOpenState, scrollEventObj, dcapiConfig, config } = props;

    this._setDocumentOpenState(documentOpenState);
    this._setAppConfig(appConfig);
    this._setFeatureEnability(props);
    this._setUIControlsConfig(UIControlsConfig);
    this._registerCallbackOnViewSDKScroll(scrollEventObj);
    this._setDCAPIConfig(dcapiConfig);
    this._setCustomReadingThemesSet(config);
  }

  _setCustomReadingThemesSet(config) {
    this.stores.pageThemeSwitcherStore.setCustomReadingThemesSet(config?.customReadingThemesSet || []);
  }

  _exposeTestApis() {
    if (getQueryParameterFromUrl(window.location.href, "exposeTestApis") === "true") {
      this._testApiHandlerPromise = import("utils/test-api").then(TestApi => {
        const testApiHandler = TestApi.handleTestApis(this._viewingOrigin);
        window.handleTestApis = testApiHandler;
        return testApiHandler;
      });
    }
  }

  _enableSlideMode() {
    const isSlideModeEnabledVal = getQueryParameterFromUrl(window.location.href, "enableSlideMode");
    if (isSlideModeEnabledVal === "true") {
      this.stores.documentStore.setViewMode(docViewModes.SINGLE_PAGE);
    }
  }

  _enableAcroForm() {
    const enableAcroForm = getQueryParameterFromUrl(window.location.href, "enableAcroForm");
    if (!isUndefined(enableAcroForm)) {
      this.stores.documentStore.setAcroFormVisibility(
        enableAcroForm === "true" ? true : enableAcroForm === "false" ? false : getShouldRenderAcroForm(this.stores)
      );
    }
  }

  _shouldEnablePersonalCommenting() {
    const enablePersonalCommenting = getQueryParameterFromUrl(window.location.href, "shouldEnablePersonalCommenting");
    if (!isUndefined(enablePersonalCommenting)) {
      this.stores.overlayDropinStore.setShouldRenderPersonalCommentsDropin(
        enablePersonalCommenting === "true"
          ? true
          : enablePersonalCommenting === "false"
          ? false
          : getShouldRenderPersonalCommentsDropin(this.stores)
      );
    }
  }

  _enableTouchContextMenu(config) {
    const { disableContextMenuAutoTrigger } = config;
    const enableContextMenuOnTouchDevices =
      getQueryParameterFromUrl(window.location.href, "enableTouchContextMenu") !== "false";
    if (disableContextMenuAutoTrigger === true || enableContextMenuOnTouchDevices === false) {
      this.stores.touchSelStore.setShouldEnableTouchContextMenu(false);
    }
  }

  _setUseReactVirtualized(config) {
    const reactVirtualizedQueryParam = getQueryParameterFromUrl(window.location.href, "useReactVirtualized");

    const assetType = getAssetType();
    let useReactVirtualized = false;
    if (reactVirtualizedQueryParam && reactVirtualizedQueryParam !== "undefined") {
      useReactVirtualized = reactVirtualizedQueryParam === "true";
    } else if (!getIsInPlaceMode(this.stores)) {
      useReactVirtualized =
        (getShouldUseModernViewer(this.stores) || assetType !== "/review") && config.enableReactVirtualized;
    }
    this.stores.documentStore.setUseReactVirtualized(useReactVirtualized);
  }

  _enableTiling() {
    const isTilingEnabledQueryParam = getQueryParameterFromUrl(window.location.href, "enableTiling");
    this.stores.documentStore.setIsTilingEnabled(isTilingEnabledQueryParam !== "false");
  }

  _setUndoButtonVisibility(config) {
    const isCommentingEnabled = isPersonalCommentingEnabled(this.stores);
    if (isCommentingEnabled) {
      // if personal commenting is enabled set comment panel visibility
      setShowCommentsPanel(isCommentingEnabled, this.stores);
      if (!isUndefined(config) && !isUndefined(config.shouldShowUndoButtonInLHP)) {
        this.stores.lhpStore.setUndoButtonVisibility(config.shouldShowUndoButtonInLHP);
      } else {
        // default value
        this.stores.lhpStore.setUndoButtonVisibility(isCommentingEnabled);
      }
    }
  }

  _setRedoButtonVisibility(config) {
    const isCommentingEnabled = isPersonalCommentingEnabled(this.stores);
    if (isCommentingEnabled) {
      if (!isUndefined(config) && !isUndefined(config.shouldShowRedoButtonInLHP)) {
        this.stores.lhpStore.setRedoButtonVisibility(config.shouldShowRedoButtonInLHP);
      } else {
        this.stores.lhpStore.setRedoButtonVisibility(isCommentingEnabled);
      }
    }
  }

  _setShowScrubber() {
    if (
      (isCurrentDeviceMobile() || getShouldUseMobileModernViewer(this.stores)) &&
      (getViewMode(this.stores) === docViewModes.CLASSIC_VIEW || getViewMode(this.stores) === docViewModes.NON_PDF)
    ) {
      this.stores.pagedViewState.setShowScrubber(true);
    }
  }

  _setDefaultRHPFeature(defaultRHPFeature) {
    const selectedDropin = getSelectedDropinForRHP(defaultRHPFeature);
    if (selectedDropin === rhpDropinIds.COMMENTS) {
      getFeatureFlagApis()
        .getFeatureFlag(EDIT_FRICTIONLESS_EXPERIMENT_TARGET_ID)
        .then(isTargetEnabled => {
          if (isTargetEnabled !== true) {
            this.stores.appStore.setSelectedDropinForRHP(selectedDropin);
          }
        });
    } else {
      this.stores.appStore.setSelectedDropinForRHP(selectedDropin);
    }
  }

  _setRhpSkeletonDropinId(dropin) {
    this.stores.appStore.setRhpSkeletonDropinId(dropin);
  }

  _enableTrove() {
    const enableTrove = getQueryParameterFromUrl(window.location.href, "enableTrove");
    if (enableTrove === "true") {
      this.stores.overlayDropinStore.setShouldRenderTroveDropin(true);
    }
  }

  _enableEncryptedWrite(config) {
    let { enableEncryptedWrite } = config;
    if (!isUndefined(enableEncryptedWrite)) {
      this.stores.overlayDropinStore.setShouldUseEncryptedWrite(enableEncryptedWrite);
    }
  }

  _setStickyToolPanelBehavior(config) {
    const { hideStickyToolOnTap } = config;
    if (!isUndefined(hideStickyToolOnTap)) {
      this.stores.appStore.setHideStickyToolOnTap(hideStickyToolOnTap);
    }
  }

  _suppressPasswordDialog(config) {
    let { suppressPasswordDialog } = config;
    if (!isUndefined(suppressPasswordDialog)) {
      this.stores.appStore.setSuppressPasswordDialog(suppressPasswordDialog);
    }
  }

  _enableDarkModeConfigs(config) {
    let { enableThemeSwitcher } = config;
    this.stores.appStore.setEnableThemeSwitcher(enableThemeSwitcher);
  }

  _setFallbackPrintTimeout(config) {
    let { fallbackPrintTimeout } = config;
    if (!isUndefined(fallbackPrintTimeout) && isNumber(fallbackPrintTimeout) && fallbackPrintTimeout > 0) {
      this.stores.appStore.setFallbackPrintTimeout(fallbackPrintTimeout);
    }
  }

  _enableCopyWithFormatting() {
    const enableCopyWithFormatting = getQueryParameterFromUrl(window.location.href, "copyWithFormatting");
    if (enableCopyWithFormatting === "true") {
      this.stores.appStore.enableCopyWithFormatting(true);
    }
  }

  _enableFillSign(config) {
    isFillSignEnabled(config).then(enableFillSign => {
      this.stores.overlayDropinStore.setShouldRenderFillSignDropin(enableFillSign);
    });
  }

  _enablePdfRequestSignatures(config) {
    isPdfRequestSignaturesEnabled(config).then(enablePdfRequestSignatures => {
      this.stores.overlayDropinStore.setShouldRenderPdfRequestSignaturesDropin(enablePdfRequestSignatures);
    });
    getFeatureFlagApis()
      .getFeatureFlag("enable-pdf-request-signatures")
      .then(enablePdfRequestSignatures => {
        this.stores.overlayDropinStore.setShouldRenderPdfRequestSignaturesDropin(enablePdfRequestSignatures);
      });
  }

  _enableImageSelection() {
    getFeatureFlagApis()
      .getFeatureFlag(IMAGE_SELECTION_PARAMS.EXPERIMENT_FLAG)
      .then(experiment => {
        if (
          experiment === IMAGE_SELECTION_PARAMS.OPTIONS.ENABLE_EDIT ||
          experiment === IMAGE_SELECTION_PARAMS.OPTIONS.ENABLE_REPLACE
        ) {
          this.stores.overlayDropinStore.setEnableImageSelectionConfig(true);
        }
      });
  }

  _setShouldDeferLoadOverlays(config) {
    let { shouldDeferLoadOverlays } = config;

    if (!isUndefined(shouldDeferLoadOverlays) && isBoolean(shouldDeferLoadOverlays)) {
      this.stores.overlayDropinStore.setShouldDeferLoadOverlays(shouldDeferLoadOverlays);
    }
  }

  _enableBookmarks() {
    const enableBookmarks = getQueryParameterFromUrl(window.location.href, "enableBookmarks");
    this.stores.bookmarkStore.setShouldEnableBookmarks(
      enableBookmarks === "true" ? true : enableBookmarks === "false" ? false : shouldEnableBookmarks(this.stores)
    );
  }

  _enableMSAL() {
    // Disabled with build flag SHOULD_ENABLE_MSAL
    const enableMSAL = getQueryParameterFromUrl(window.location.href, "enableMSAL");
    if (enableMSAL === "true" && SHOULD_ENABLE_MSAL) {
      this.stores.mipStore.enableMSAL(true);
    }
  }

  _setShouldEnableVoiceAnnotations() {
    const shouldEnableVoiceAnnotations = getQueryParameterFromUrl(window.location.href, "enableVoiceAnnots");
    if (!isUndefined(shouldEnableVoiceAnnotations) && shouldEnableVoiceAnnotations === "true") {
      this.stores.overlayDropinStore.setShouldEnableVoiceAnnotations(true);
    }
  }

  _enableLiquidModeView() {
    const enableLiquidModeView = getQueryParameterFromUrl(window.location.href, "enableLiquidModeView");
    if (enableLiquidModeView === "true") {
      this.stores.liquidModeViewStore.enableLiquidModeView(true);
    }
  }

  _enableInlineOrganize(enableInlineOrganize) {
    const enableInlineOrganizefromUrl = getQueryParameterFromUrl(window.location.href, "enableInlineOrganize");
    if (enableInlineOrganizefromUrl === "true" || enableInlineOrganize) {
      this.stores.appStore.enableInlineOrganize(true);
      this.stores.appStore.setShouldUseNewThumbnailsGrid(true);
    }
    getFeatureFlagApis()
      .getFeatureFlag("enable-inline-organize")
      .then(enableInlineOrganise => {
        if (enableInlineOrganise) {
          this.stores.appStore.enableInlineOrganize(true);
          this.stores.appStore.setShouldUseNewThumbnailsGrid(true);
        }
      });
  }

  _disableProtectedPDFCancelButton(shouldDisable) {
    if (shouldDisable) {
      this.stores.appStore.disableProtectedPDFCancelButton(true);
    }
  }

  _setUseNewThumbnailsGrid(useNewThumbnailsGrid) {
    const useNewThumbnailsGridFromUrl = getQueryParameterFromUrl(window.location.href, "useNewThumbnailsGrid");
    if (useNewThumbnailsGridFromUrl === "true" || useNewThumbnailsGrid) {
      this.stores.appStore.setShouldUseNewThumbnailsGrid(true);
    }
  }

  _enableDarkPageTheme() {
    const pageTheme = getQueryParameterFromUrl(window.location.href, "page_theme");
    if (pageTheme && pageTheme === "dark") {
      this._setDarkThemeEnabled(true);
    } else if (pageTheme && pageTheme === "light") {
      this._setDarkThemeEnabled(false);
    }
  }
  _setDXVersion() {
    const hostEnv = envUtil.getHostEnv();
    if (hostEnv !== "prod") {
      const dxVersion = getQueryParameterFromUrl(window.location.href, "dxVersion");
      if (dxVersion && dxVersion.length) {
        this.stores.liquidModeViewStore.setDXVersion(dxVersion);
      }
    }
  }

  _setUseMinifiedDX() {
    const hostEnv = envUtil.getHostEnv();
    if (hostEnv !== "prod") {
      const useMinifiedDx = getQueryParameterFromUrl(window.location.href, "useMinifiedDx");
      if (useMinifiedDx === "false") {
        this.stores.liquidModeViewStore.setUseMinifiedDX(false);
      }
    }
  }

  _setDocViewBgColor(docViewBgColor) {
    const { docViewBgColorBeforeFileLoad, docViewBgColorAfterFileLoad } = getDefaultConfig();
    this.stores.documentStore.setDocViewBgColorBeforeFileLoad(docViewBgColorBeforeFileLoad);
    if (!isUndefined(docViewBgColor) && isHexColor(docViewBgColor)) {
      this.stores.documentStore.setDocViewBgColorAfterFileLoad(docViewBgColor);
    } else {
      this.stores.documentStore.setDocViewBgColorAfterFileLoad(docViewBgColorAfterFileLoad);
    }
  }

  _setAcroFormVisibility(shouldRenderAcroForm) {
    if (!isUndefined(shouldRenderAcroForm)) {
      this.stores.documentStore.setAcroFormVisibility(shouldRenderAcroForm);
    }
  }

  _setLHPVisibility(showLHP) {
    if (!isUndefined(showLHP) && isBoolean(showLHP)) {
      this.stores.lhpStore.setLHPVisibility(showLHP);
      this.stores.lhpStore.setLHPExplicitlyDisabled(!showLHP);
    }
  }

  _setHudDisable(showHUD) {
    if (!isUndefined(showHUD) && isBoolean(showHUD)) {
      this.stores.hudStore.setHudDisable(!showHUD);
      this.stores.hudStore.setHudExplicitlyDisabled(!showHUD);
    }
  }

  _setQuickToolsVisibility(showQuickTools) {
    if (!isUndefined(showQuickTools) && isBoolean(showQuickTools)) {
      this.stores.documentStore.setQuickToolsVisibility(showQuickTools);
    }
  }

  _setRhpVisibility(showRhp) {
    if (!isUndefined(showRhp) && isBoolean(showRhp)) {
      this.stores.documentStore.setRhpVisibility(showRhp);
    }
  }

  _setShouldRenderHighlightsDropin(path, shouldRenderHighlightsDropin) {
    // In case of external_asset no need of shouldRenderHighlightsDropin check
    if (!isExternalAsset(path) && !isUndefined(shouldRenderHighlightsDropin)) {
      this.stores.overlayDropinStore.setShouldRenderHighlightsDropin(envUtil.isProd(), false);
    }
  }

  _setUseReactVirtualizedAndShouldRenderCommentsDropin(shouldRenderCommentsDropin) {
    if (!isUndefined(shouldRenderCommentsDropin)) {
      let finalValue = shouldRenderCommentsDropin;
      if (getShouldRenderHighlightsDropin(this.stores)) {
        finalValue = false;
      } else {
        let valueFromUrl = getQueryParameterFromUrl(window.location.href, "shouldRenderCommentsDropin");
        if (!isUndefined(valueFromUrl)) {
          finalValue = valueFromUrl === "true" ? true : valueFromUrl === "false" ? false : finalValue;
        }
      }
      !finalValue && this.stores.documentStore.setUseReactVirtualized(false);
      this.stores.overlayDropinStore.setShouldRenderCommentsDropin(finalValue);
    }
  }

  _enableFitOneFullPageMode() {
    const enableFitOneFullPageMode =
      getQueryParameterFromUrl(window.location.href, "enableFitOneFullPageMode") !== "false";
    this.stores.pageByPageStore.setEnableFitOneFullPageMode(enableFitOneFullPageMode);
  }

  _setShouldRenderPersonalCommentsDropin(shouldRenderPersonalCommentsDropin) {
    if (!isUndefined(shouldRenderPersonalCommentsDropin)) {
      this.stores.overlayDropinStore.setShouldRenderPersonalCommentsDropin(shouldRenderPersonalCommentsDropin);
    }
  }

  _setShouldRenderVisualizerDropin(shouldRenderVisualizerDropin) {
    if (!isUndefined(shouldRenderVisualizerDropin)) {
      this.stores.overlayDropinStore.setShouldRenderVisualizerDropin(shouldRenderVisualizerDropin);
    }
  }

  _setShouldRenderSignAuthoringDropin(shouldRenderSignAuthoringDropin) {
    if (!isUndefined(shouldRenderSignAuthoringDropin)) {
      this.stores.overlayDropinStore.setShouldRenderSignAuthoringDropin(shouldRenderSignAuthoringDropin);
    }
  }

  _setShouldRenderSignAuthoring2Dropin(shouldRenderSignAuthoring2Dropin) {
    if (!isUndefined(shouldRenderSignAuthoring2Dropin)) {
      this.stores.overlayDropinStore.setShouldRenderSignAuthoring2Dropin(shouldRenderSignAuthoring2Dropin);
    }
  }

  _setShouldRenderESignOverlayDropin(shouldRenderESignOverlayDropin) {
    if (!isUndefined(shouldRenderESignOverlayDropin)) {
      this.stores.overlayDropinStore.setShouldRenderESignOverlayDropin(shouldRenderESignOverlayDropin);
    }
  }

  _setShouldRenderPageShadows(shouldRenderPageShadows) {
    if (!isUndefined(shouldRenderPageShadows)) {
      this.stores.appUiState.setShouldRenderPageShadows(shouldRenderPageShadows);
    }
  }

  _setDownloadButtonVisibility(shouldShowDownloadButtonInHud) {
    if (!isUndefined(shouldShowDownloadButtonInHud)) {
      this.stores.hudStore.setDownloadButtonVisibility(shouldShowDownloadButtonInHud);
    }
  }

  _setUseReactVirtualizedAndFetchAccContent(config) {
    let { enableAccessibilityByDefault } = config;

    if (enableAccessibilityByDefault) {
      this.stores.accessibilityStore.setShouldFetchAccContent(true);
      this.stores.accessibilityStore.setAccEnabledByDefault();
    }
    getFeatureFlagApis()
      .getFeatureFlag("enable-accessibility")
      .then(enableAccessibility => {
        if (enableAccessibility) {
          this.stores.accessibilityStore.setShouldFetchAccContent(true);
          this.stores.accessibilityStore.setAccEnabledByDefault();
        }
      });
  }

  _setEnableRHPResizing(config) {
    let { enableRHPResizing } = config;

    if (!isUndefined(enableRHPResizing) && isBoolean(enableRHPResizing)) {
      this.stores.appStore.setShouldEnableRHPResizing(enableRHPResizing);
    }
  }

  _setIsDocked(shouldDockHUD) {
    if (!isUndefined(shouldDockHUD) && isBoolean(shouldDockHUD)) {
      this.stores.hudStore.setIsDocked(shouldDockHUD);
    }
  }

  _setUsePrintWithWindow(printWithWindow) {
    if (!isUndefined(printWithWindow) && isBoolean(printWithWindow)) {
      this.stores.appStore.setUseWindowPrint(printWithWindow);
    }
  }

  _enableFallbackPrint(enableFallbackPrint) {
    if (!isUndefined(enableFallbackPrint) && isBoolean(enableFallbackPrint)) {
      this.stores.appStore.enableFallbackPrint(enableFallbackPrint);
    }
  }

  _setUseIframelessPrint(useIframelessPrint) {
    if (!isUndefined(useIframelessPrint) && isBoolean(useIframelessPrint)) {
      this.stores.appStore.setUseIframelessPrint(useIframelessPrint);
    }
  }

  _setShouldshowSearchInHud(shouldShowSearchInHud) {
    if (!isUndefined(shouldShowSearchInHud)) {
      this.stores.searchStore.setShouldshowSearchInHud(shouldShowSearchInHud);
    }
  }

  _setOpenAction() {
    if (this.props.useDisplayParamsForOpen === true) {
      this.stores.pagedViewState.setOpenAction(this.props.displayParams);
    }
  }

  _setPageViewMode(viewMode, zoomLevel) {
    setPageViewMode(this.stores, viewMode, zoomLevel);
  }

  _setPageZoomLevel(zoomLevel) {
    if (isNumber(zoomLevel)) {
      this.stores.zoom.setPageZoomLevel(zoomLevel);
      this.stores.zoom.disableInitialZoomLevelChange();
    }
  }

  _setViewRotation(rotationAngle) {
    const isRotationValid = angle => [0, 90, 180, 270].includes(angle);
    if (isRotationValid(rotationAngle)) this.stores.documentStore.setViewRotation(rotationAngle);
  }

  _setDarkThemeEnabled(darkThemeEnabled) {
    this.stores.pageThemeSwitcherStore.setDarkPageTheme(darkThemeEnabled);
    trackDocumenOpenedInNightMode(darkThemeEnabled);
  }

  _setDocViewTheme(docViewTheme) {
    this.stores.pageThemeSwitcherStore.setDocViewTheme(docViewTheme);
  }

  _setOpenActionAndDisableChange(pageNumber, x, y, zoomLevel, viewMode) {
    const openAction = {
      pageIndex: isNumber(pageNumber) ? pageNumber - 1 : 0,
      x: isNumber(x) ? x : 0,
      y: isNumber(y) ? y : 0,
      zoomLevel,
      viewMode
    };
    this.stores.pagedViewState.setOpenAction(openAction);
    this.stores.pagedViewState.disableOpenActionChange();
  }

  _checkIfOpenStateIsValid(documentOpenState) {
    if (!documentOpenState || Object.keys(documentOpenState).length === 0) {
      return false;
    }
    const { viewMode, zoomLevel, x, y, pageNumber, rotationAngle } = documentOpenState;
    if (
      viewMode ||
      isNumber(zoomLevel) ||
      isNumber(x) ||
      isNumber(y) ||
      isNumber(pageNumber) ||
      isNumber(rotationAngle)
    ) {
      return true;
    }
    return false;
  }

  _setDocumentOpenState(documentOpenState) {
    if (this._checkIfOpenStateIsValid(documentOpenState)) {
      const { viewMode, zoomLevel: zoomInPercentage, x, y, pageNumber, rotationAngle } = documentOpenState;
      const zoomLevel = zoomInPercentage / 100;
      this._setPageZoomLevel(zoomLevel);
      this._setPageViewMode(viewMode, zoomLevel);
      this._setViewRotation(rotationAngle);
      this._setOpenActionAndDisableChange(pageNumber, x, y, zoomLevel, viewMode);
    }

    if (documentOpenState) {
      const { darkThemeEnabled, docViewTheme } = documentOpenState;
      if (isBoolean(documentOpenState.darkThemeEnabled)) this._setDarkThemeEnabled(darkThemeEnabled);
      const pageTheme = getQueryParameterFromUrl(window.location.href, "page_theme");
      if (!pageTheme) {
        getFeatureFlagApis()
          .getFeatureFlag("preview-config")
          .then(previewConfig => {
            if (
              previewConfig &&
              previewConfig.documentOpenState &&
              isBoolean(previewConfig.documentOpenState.darkThemeEnabled)
            )
              this._setDarkThemeEnabled(previewConfig.documentOpenState.darkThemeEnabled);
          });
        if (isNonEmptyString(documentOpenState.docViewTheme)) this._setDocViewTheme(docViewTheme);
      }
    }
  }

  _setCurrLHPFeature(defaultLHPFeature) {
    if (!isUndefined(defaultLHPFeature)) {
      if (lhpFeaturesForDefaultView.hasOwnProperty(defaultLHPFeature)) {
        const lhpFeature = lhpFeaturesForDefaultView[defaultLHPFeature];
        this.stores.lhpStore.setCurrLHPFeature(lhpFeature);
      }
    }
  }

  _setAppConfig(appConfig) {
    if (!isUndefined(appConfig)) {
      const {
        showLHP,
        showHUD,
        dockHUD,
        defaultLHPFeature,
        defaultRHPFeature,
        useIframelessPrint,
        printWithWindow,
        enableFallbackPrint,
        enableInlineOrganize,
        useNewThumbnailsGrid,
        showQuickTools,
        showRHP,
        caretMode,
        scrollToShowCaret,
        showCaretOnPDFOpen,
        disableProtectedPDFCancelButton
      } = appConfig;
      this._setLHPVisibility(showLHP);
      this._setHudDisable(showHUD);
      this._setIsDocked(dockHUD);
      this._setCurrLHPFeature(defaultLHPFeature);
      this._setDefaultRHPFeature(defaultRHPFeature);
      this._setUseIframelessPrint(useIframelessPrint);
      this._enableFallbackPrint(enableFallbackPrint);
      this._setUsePrintWithWindow(printWithWindow);
      this._enableInlineOrganize(enableInlineOrganize);
      this._disableProtectedPDFCancelButton(disableProtectedPDFCancelButton);
      this._setUseNewThumbnailsGrid(useNewThumbnailsGrid);
      this._setQuickToolsVisibility(showQuickTools);
      this._setRhpVisibility(showRHP);
      this._setCaretMode(caretMode, scrollToShowCaret, showCaretOnPDFOpen);
    }
  }

  _setFeatureEnability(config) {
    const { featureConfig } = config;
    if (!isUndefined(featureConfig)) {
      for (let featureName in featureConfig) {
        const value = featureConfig[featureName];
        setFeatureEnability(this.stores, featureName, value, config);
      }
    }
  }

  _setUIControlsConfig(UIControlsConfig) {
    if (!isUndefined(UIControlsConfig)) {
      for (let componentName in UIControlsConfig) {
        const config = UIControlsConfig[componentName];
        const customizerArray = getUIControlCustomizer(this.stores, componentName);
        if (Array.isArray(customizerArray)) {
          customizerArray.forEach(customizerFn => customizerFn(componentName, config));
        }
      }
      if (!(appIdentifiers.FILE_NAME_TOAST in UIControlsConfig)) {
        // set default mode for file name toast if prop is not passed
        if (isCurrentDeviceMobile() || getShouldUseMobileModernViewer(this.stores)) {
          this.stores.documentStore.setDisplayToast(true);
        }
      }
    }
  }

  _setShowHudOnScrollTop() {
    this.showHudOnScrollStop = debounce(() => {
      this.stores.appStore.setIsParentWebsiteScrolling(false);
    }, 250);
  }

  _handleScrollEventFromViewSDK(evt) {
    const data = evt.data;
    // we are updating stores only if difference of scrollTop is more than 3px because on firefox mobile, multiple scroll events with minor difference of scrollTop are coming which results in HUD flickering
    if (
      !(Math.abs(getPagesViewYPos(this.stores) - data.viewTop) < 3) ||
      data.viewHeight !== getDocumentVisibleHeight(this.stores)
    ) {
      this.stores.appStore.setIsParentWebsiteScrolling(true);
      this.stores.documentStore.setTriggerDocumentViewUpdateEvent(false);
      this.stores.pagedViewState.scrollPagesView(0, data.viewTop);
      this.stores.appStore.setWindowHeight(data.windowHeight);
      this.stores.documentViewStore.setDocumentVisibleHeight(data.viewHeight);
      this.showHudOnScrollStop();
    }
    this.stores.hudStore.setShouldUpdateHUDPosition(true);
  }

  _registerCallbackOnViewSDKScroll(scrollEventObj) {
    if (!isUndefined(scrollEventObj) && !isNull(scrollEventObj)) {
      if (getIsInPlaceMode(this.stores)) {
        this.scrollEventObj = scrollEventObj;
        this.scrollEventObj.on(VIEW_SDK_SCROLL, this._handleScrollEventFromViewSDK.bind(this));
      }
    }
  }

  _setDCAPIRenditionSize(dcapiRenditionSize) {
    if (isNumber(dcapiRenditionSize) && !isNaN(dcapiRenditionSize) && dcapiRenditionSize > 0) {
      this.stores.dcapiStore.setDCAPIRenditionSize(dcapiRenditionSize);
    }
  }

  _setDCAPIRenditionFormat(dcapiRenditionFormat) {
    if (isNonEmptyString(dcapiRenditionFormat)) {
      const validFormats = ["jpg", "png"];
      if (validFormats.includes(dcapiRenditionFormat)) {
        this.stores.dcapiStore.setDCAPIRenditionFormat(dcapiRenditionFormat);
      }
    }
  }

  _setDCAPIConfig(dcapiConfig) {
    if (!isUndefined(dcapiConfig)) {
      const { dcapiRenditionSize, dcapiRenditionFormat } = dcapiConfig;
      this._setDCAPIRenditionSize(dcapiRenditionSize);
      this._setDCAPIRenditionFormat(dcapiRenditionFormat);
    }
  }

  _setCaretMode(caretMode, scrollToShowCaret, showCaretOnPDFOpen) {
    // Temp code to test in DC-Web. In future, we would only support config.
    const defaultCaretModeQueryParam = getQueryParameterFromUrl(window.location.href, "caretMode");
    const isCaretMode = defaultCaretModeQueryParam === "true" || caretMode;
    this.stores.caretModeStore.setCaretMode(isCaretMode, true);
    this.stores.caretModeStore.setShouldScrollToShowCaret(scrollToShowCaret);
    this.stores.caretModeStore.setShowCaretOnPDFOpen(showCaretOnPDFOpen);
  }

  _initProgressiveRenderingSettings(config = {}) {
    let { shouldUseProgressiveRendering = true } = config;
    let progressiveRenderingStoreValue = false;
    // We will check for if window.offscreen supported and browser is not firefox
    if (shouldEnableProgressiverendering()) {
      if (shouldUseProgressiveRendering) {
        progressiveRenderingStoreValue = true;
      }
      const useProgressiveRendering = getQueryParameterFromUrl(window.location.href, "useProgressiveRendering");
      // Check to disable progressive rendering via query params if specifically set to false
      if (useProgressiveRendering === "false") {
        progressiveRenderingStoreValue = false;
      } else if (useProgressiveRendering === "true") {
        progressiveRenderingStoreValue = true;
      }
    }
    this.stores.documentStore.setUseProgressiveRendering(progressiveRenderingStoreValue);
    const progressiveRenderInterval = getQueryParameterFromUrl(window.location.href, "progressiveTimeLimit");
    this.stores.documentStore.setProgressiveRenderInterval(progressiveRenderInterval);

    const progressiveRenderMaxInterval = getQueryParameterFromUrl(window.location.href, "progressiveMaxTimeLimit");
    this.stores.documentStore.setProgressiveRenderMaxInterval(progressiveRenderMaxInterval);

    const renderIntervalMultiplier = getQueryParameterFromUrl(window.location.href, "progressiveMultiplier");
    this.stores.documentStore.setRenderIntervalMultiplier(renderIntervalMultiplier);
  }

  // Determines whether to enable the NBA v2 experience or fallback to the NBA v1 experience based on configuration, query parameters, and feature flags.
  async _setShouldShowBottomBanner(config = {}) {
    const { bottomBannerConfig = {} } = config;
    const { dropinId: bottomBannerDropinId, shouldForceEnable } = bottomBannerConfig;

    const disableBottomBanner = getQueryParamValue("disableBottomBanner") === "true";

    if (disableBottomBanner || !bottomBannerDropinId) {
      this.stores.bottomBoard.setShouldShowBottomBanner(false);
      return;
    }

    try {
      const isForceEnabled = shouldForceEnable ? await shouldForceEnable : false;

      if (isForceEnabled) {
        this.stores.bottomBoard.setShouldShowBottomBanner(true);
        return;
      }

      const featureFlagValue = await getFeatureFlagApis().getFeatureFlag("DCWeb_GenAI_Collab_NBA");
      const shouldShowBottomBanner = featureFlagValue === "genai_collab_nba";
      this.stores.bottomBoard.setShouldShowBottomBanner(shouldShowBottomBanner);
    } catch (error) {
      this.stores.bottomBoard.setShouldShowBottomBanner(false);
    }
  }

  _setShouldRenderNotificationBannerBeforeDoc(config) {
    const { renderNotificationBannerBeforeDoc } = config;
    if (!isUndefined(renderNotificationBannerBeforeDoc && isBoolean(renderNotificationBannerBeforeDoc))) {
      this.stores.notificationViewStore.setShouldRenderBannerBeforeDoc(renderNotificationBannerBeforeDoc);
    }
  }
}

export { EnableFeatureController };
